import React, { useEffect, useState } from "react";
import { getApiUrl } from "../utils/apiConfig";
import { 
  detectBotBehavior, 
  verifyUserInteraction, 
  humanDelay, 
  createHoneypot,
  generateFingerprint 
} from "../utils/security";

const API_URL = getApiUrl();

export default function RecaptchaPage() {
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [honeypot] = useState(createHoneypot());
  const [startTime] = useState(Date.now());

  useEffect(() => {
    // Check if running on localhost
    const hostname = window.location.hostname;
    const isLocal = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
    setIsLocalhost(isLocal);

    // Only load reCAPTCHA if not on localhost
    if (!isLocal) {
      // Load the reCAPTCHA script dynamically
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      // Cleanup when component unmounts
      return () => {
        const existingScript = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
        if (existingScript) {
          document.body.removeChild(existingScript);
        }
      };
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    
    try {
      // Add human-like delay
      await humanDelay(200, 500);
      
      // Check honeypot field
      const honeypotValue = document.querySelector(`input[name="${honeypot.fieldName}"]`)?.value;
      if (honeypotValue !== honeypot.expectedValue) {
        console.warn("Honeypot triggered");
        // Don't show error, just silently fail for bots
        setTimeout(() => window.location.href = "/", 3000);
        return;
      }
      
      // Check bot behavior
      const botCheck = detectBotBehavior();
      if (botCheck.isSuspicious && !isLocalhost) {
        setError("Security check failed. Please try using a different browser.");
        setIsSubmitting(false);
        return;
      }
      
      // Verify user interaction
      const hasInteracted = verifyUserInteraction();
      const timeSpent = Date.now() - startTime;
      
      if (!hasInteracted && timeSpent < 3000 && !isLocalhost) {
        setError("Please take a moment to review the page.");
        setIsSubmitting(false);
        return;
      }
      
      // Skip captcha verification on localhost
      if (isLocalhost) {
        const btn = document.getElementById("submit-btn");
        if (btn) btn.disabled = true;
        window.location.href = "/booking-form";
        return;
      }

      const token = window.grecaptcha?.getResponse();

      if (!token || token.length === 0) {
        setError("Please complete the reCAPTCHA.");
        setIsSubmitting(false);
        return;
      }

      // Disable button
      const btn = document.getElementById("submit-btn");
      if (btn) btn.disabled = true;

      // Verify reCAPTCHA token server-side
      try {
        const fingerprint = await generateFingerprint();
        
        const resp = await fetch(`${API_URL}/api/verify-recaptcha`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            token,
            fingerprint,
            timestamp: Date.now(),
            userAgent: navigator.userAgent
          })
        });
        const response = await resp.json();

        if (response.success) {
          // Store verification in session
          sessionStorage.setItem("captcha_verified", "true");
          sessionStorage.setItem("captcha_timestamp", Date.now().toString());
          
          window.location.href = "/booking-form";
        } else {
          setError("Verification failed. Please try again.");
          if (btn) btn.disabled = false;
          window.grecaptcha?.reset();
          setIsSubmitting(false);
        }
      } catch (apiError) {
        console.error("Verification error:", apiError);
        // Fallback: Allow access but log the issue
        sessionStorage.setItem("captcha_bypass", "true");
        window.location.href = "/booking-form";
      }
    } catch (error) {
      console.error("Submit error:", error);
      setError("An error occurred. Please try again.");
      setIsSubmitting(false);
      const btn = document.getElementById("submit-btn");
      if (btn) btn.disabled = false;
    }
  };

  return (
    <div className="recaptcha-page" style={{ overflowY: "auto" }}>
      <div className="greyBg"></div>
      <div className="dzSNzg">
        <div className="jKExlm">
          <form onSubmit={handleSubmit}>
            <div className="fBJhQh">
              <div className="hGbcNq">Security Check</div>
              
              {/* Honeypot field - hidden from users, visible to bots */}
              <input
                type="text"
                name={honeypot.fieldName}
                defaultValue={honeypot.expectedValue}
                style={{ 
                  position: "absolute", 
                  left: "-9999px", 
                  width: "1px", 
                  height: "1px",
                  opacity: 0,
                  pointerEvents: "none"
                }}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
              />
              
              {error && (
                <div style={{ 
                  padding: "0.75rem", 
                  marginBottom: "1rem", 
                  backgroundColor: "#fee", 
                  border: "1px solid #fcc",
                  borderRadius: "4px",
                  color: "#c33",
                  fontSize: "14px"
                }}>
                  {error}
                </div>
              )}
              
              {!isLocalhost && (
                <div className="captcha">
                  <div
                    className="g-recaptcha"
                    data-sitekey="6LfBzHYsAAAAAMdxDBtUa7Y7JNnv5WpEdnLzVGS1"
                  ></div>
                </div>
              )}
              {isLocalhost && (
                <div className="captcha" style={{ color: "#4a4a4a", fontSize: "14px", padding: "1rem" }}>
                  Running on localhost - captcha verification skipped
                </div>
              )}

              <p className="eqZafL">
                This helps us to combat harmful conduct, detect and prevent spam,
                and maintain the integrity of our products.
              </p>
              <p className="eqZafL">
                We've used Google’s reCAPTCHA Enterprise product to provide this
                security check. Use of reCAPTCHA is subject to the Google Privacy
                Policy and Terms of Use.
              </p>
              <p className="eqZafL">
                reCAPTCHA Enterprise collects hardware and software information
                such as device and application data, and sends it to Google to
                provide, maintain, and improve reCAPTCHA Enterprise and for
                general security purposes. This information is not used by Google
                for personalized advertising.
              </p>

              <div className="pt-4">
                <button
                  id="submit-btn"
                  type="submit"
                  className="dZasNV"
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: isSubmitting ? "#ccc" : "rgb(0, 100, 224)",
                    color: "white",
                    cursor: isSubmitting ? "not-allowed" : "pointer"
                  }}
                >
                  {isSubmitting ? "Verifying..." : "Continue"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Inline CSS (you can move this to a .css file) */}
      <style>{`
        .greyBg {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          background-color: #f1f2f7;
          z-index: -1;
        }
        .dzSNzg {
          height: 100dvh;
          width: 100%;
          display: grid;
          place-items: center;
        }
        .jKExlm {
          background: white;
          border: 1px solid rgb(189,189,189);
          border-radius: 0.5rem;
          max-width: 500px;
          width: calc(100% - 2rem);
          margin: auto;
        }
        .fBJhQh {
          padding: 1rem;
          background-color: #fff;
          border-radius: 0.5rem;
        }
        .hGbcNq {
          margin-bottom: 10px;
          font-size: 30px;
          font-weight: 500;
          color: rgb(74, 74, 74);
        }
        .eqZafL {
          margin-top: 10px;
          font-size: 14px;
          font-weight: 500;
          color: rgb(74, 74, 74);
        }
        .dZasNV {
          border: none;
          font-size: 13px;
          font-weight: 500;
          padding: 11px 14px;
          width: 100%;
          border-radius: 8px;
          line-height: 1;
        }
        .captcha {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 74px;
          margin-bottom: 1rem;
        }
      `}</style>
    </div>
  );
}
